/*
  Autoplay dos vídeos NATIVOS da galeria da PDP.

  O Dawn embrulha todo vídeo num <deferred-media>: o <video> só entra no DOM ao
  clicar no poster. Observamos o SLIDE (.product__media-item) — não o
  <deferred-media>, que colapsa a 0x0 no mobile antes de carregar — e, quando o
  slide entra na viewport, chamamos loadContent e tocamos o vídeo.

  O autoplay é sempre mudo. O botão de som desmuta o vídeo visível diretamente
  no gesto do cliente; ao sair da viewport, ele volta ao estado mudo. Assim o
  próximo vídeo não começa com som e o WebKit não pausa por desmute automático.
  O botão próprio de som substitui os controles nativos só após o JS assumir o vídeo;
  sem JS ou com movimento reduzido, o poster abre o player nativo com controles.

  iOS/WebKit: o <video> é injetado via cloneNode do <template>, e o WebKit
  costuma NÃO herdar a *propriedade* `muted` do atributo do markup — então o
  play() é rejeitado como "autoplay com som". Por isso o mudo é aplicado por
  propriedade (prepare/tryPlay), não só por atributo.

  O Dawn chama window.pauseAllMedia() ao trocar variante/slide e abrir modal.
  Pausas externas re-sincronizam o autoplay mudo. Fullscreen e
  picture-in-picture ficam sob controle do sistema.

  - Só vídeos nativos (upload Shopify); externos usam <iframe> e ficam de fora.
  - Respeita prefers-reduced-motion e a pausa do usuário na UI do sistema.
    Não religa por trás do modal de zoom aberto.
  - Idempotente: re-observa nós novos em shopify:section:load (editor de tema).
  - Diagnóstico: adicione ?ohlivideodebug à URL para ver um painel na tela com o
    que está acontecendo (útil para depurar em aparelho real). Não afeta clientes.
*/
(function () {
  var DEBUG = /[?&]ohlivideodebug/.test(location.search);
  var panel;
  function dbg(m) {
    if (!DEBUG) return;
    if (!panel) {
      panel = document.createElement('div');
      panel.style.cssText =
        'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;background:rgba(0,0,0,.85);' +
        'color:#0f0;font:11px/1.35 monospace;padding:6px 8px;max-height:45vh;overflow:auto;white-space:pre-wrap';
      (document.body || document.documentElement).appendChild(panel);
    }
    panel.textContent += m + '\n';
    panel.scrollTop = panel.scrollHeight;
  }

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  dbg('init · reduceMotion=' + reduced + ' · UA=' + navigator.userAgent.slice(0, 60));
  if (reduced) {
    dbg('ABORT: prefers-reduced-motion');
    return;
  }

  // Já inicializado nesta página? Só re-escaneia nós novos e sai (idempotência).
  if (window.__ohliPdpVideoAutoplay) {
    window.__ohliPdpVideoAutoplay.rescan();
    return;
  }

  var THRESHOLD = 0.25; // 50% era alto demais p/ vídeo vertical maior que a viewport
  var targets = []; // .product__media-item de vídeo nativo já observados
  var visible = new Set(); // os slides que estão >= THRESHOLD visíveis agora

  // Retorna o <deferred-media> de vídeo NATIVO dentro do slide, ou null.
  // Vídeo nativo traz um <video> no template; externo traz <iframe>.
  function nativeVideoDeferred(item) {
    var deferred = item.querySelector('deferred-media');
    if (!deferred) return null;
    var tpl = deferred.querySelector('template');
    return tpl && tpl.content.querySelector('video') ? deferred : null;
  }

  function modalOpen() {
    // Enquanto o zoom estiver aberto, não religamos o vídeo da galeria (atrás dele).
    return !!document.querySelector('product-modal[open]');
  }

  // Fullscreen / PiP: a UI é do sistema operacional e não gera evento de
  // ponteiro na página, então uma pausa lá seria lida como externa e religada
  // por cima do cliente. Nesses estados não dirigimos o vídeo.
  function ownedBySystemUi(v) {
    return (
      document.fullscreenElement === v ||
      document.webkitFullscreenElement === v ||
      v.webkitDisplayingFullscreen === true ||
      document.pictureInPictureElement === v
    );
  }

  function soundOn(v) {
    return v.dataset.ohliSoundOn === '1';
  }

  function action(item, name) {
    return item.querySelector('.ohli-pdp-video-action--' + name);
  }

  function updateActions(v) {
    var item = v.closest('.product__media-item');
    if (!item) return;
    var sound = action(item, 'sound');
    if (sound) {
      var audible = soundOn(v) && !v.muted;
      sound.setAttribute('aria-pressed', String(audible));
      sound.setAttribute('aria-label', audible ? 'Desativar som do vídeo' : 'Ativar som do vídeo');
    }
  }

  function resetSound(v) {
    if (!soundOn(v)) return;
    delete v.dataset.ohliSoundOn;
    v.muted = true;
    v.defaultMuted = true;
    updateActions(v);
  }

  // Autoplay exige mudo por PROPRIEDADE (o WebKit não herda do atributo no
  // clone do <template>). Nunca desmutamos fora do clique no botão.
  function enforceMuted(v) {
    if (soundOn(v)) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
  }

  function tryPlay(v, tag) {
    enforceMuted(v);
    var p = v.play();
    if (p && typeof p.then === 'function') {
      p.then(function () {
        dbg('play OK' + tag + ' (muted=' + v.muted + ')');
      }).catch(function (err) {
        dbg('play ERR' + tag + ' ' + (err && err.name));
        updateActions(v);
      });
    }
  }

  function playVideo(v) {
    if (!v) return;
    // iOS/WebKit exige playsinline (atributo) além do mudo no play() sem gesto.
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    enforceMuted(v);

    // preload="none": no iOS o play() sem gesto não busca os bytes → sem frame
    // para tocar. Força o carregamento antes de tentar.
    if (v.preload === 'none' || v.networkState === 0 /* NETWORK_EMPTY */) {
      v.preload = 'auto';
      try { v.load(); } catch (e) {}
    }

    tryPlay(v, '');
    // Retry diferido: por frame (falta de layout) e quando os dados chegarem.
    // Não religa se o cliente pausou na UI do sistema.
    requestAnimationFrame(function () {
      if (v.paused && drivable(v)) tryPlay(v, ' (raf)');
    });
    if (v.readyState < 2 /* HAVE_CURRENT_DATA */) {
      v.addEventListener(
        'loadeddata',
        function once() {
          v.removeEventListener('loadeddata', once);
          if (v.paused && drivable(v) && visible.has(v.closest('.product__media-item'))) {
            tryPlay(v, ' (loadeddata)');
          }
        },
        { once: true }
      );
    }
  }

  // Podemos dirigir este vídeo agora? (fonte única das condições de autoplay)
  function drivable(v) {
    return (
      !v.dataset.ohliUserPaused &&
      !soundOn(v) && // áudio requer o gesto no próprio botão
      !document.hidden &&
      !modalOpen() &&
      !ownedBySystemUi(v)
    );
  }

  function wireVideo(v) {
    if (!v || v.dataset.ohliWired) return;
    v.dataset.ohliWired = '1';
    v.controls = false;
    var item = v.closest('.product__media-item');
    var actions = item && item.querySelector('.ohli-pdp-video-actions');
    if (actions) actions.hidden = false;
    if (DEBUG) {
      ['loadstart', 'loadeddata', 'playing', 'waiting', 'stalled', 'suspend', 'error', 'abort'].forEach(function (ev) {
        v.addEventListener(ev, function () {
          dbg('vid:' + ev + ' net=' + v.networkState + ' rs=' + v.readyState + (v.error ? ' err=' + v.error.code : ''));
        });
      });
    }

    v.addEventListener('volumechange', function () {
      if (v.muted) delete v.dataset.ohliSoundOn;
      updateActions(v);
    });

    v.addEventListener('pause', function () {
      // Pausa que NÓS disparamos (saída da viewport): já tratada.
      if (v.dataset.ohliProgrammatic) {
        delete v.dataset.ohliProgrammatic;
        updateActions(v);
        return;
      }
      // Fullscreen/PiP: a pausa é do cliente na UI do sistema, sem evento de
      // ponteiro na página. Respeita e não religa.
      if (ownedBySystemUi(v)) {
        v.dataset.ohliUserPaused = '1';
        updateActions(v);
        dbg('pausa do cliente (fullscreen/PiP)');
        return;
      }
      if (v.dataset.ohliUserPaused) {
        updateActions(v);
        dbg('pausa do cliente (botão)');
        return;
      }
      // Pausa externa (Dawn/modal) → volta ao autoplay mudo quando permitido.
      resetSound(v);
      sync();
    });

    v.addEventListener('play', function () {
      // Qualquer reprodução real limpa flag de pausa presa (rede de segurança).
      delete v.dataset.ohliUserPaused;
      updateActions(v);
    });
    updateActions(v);
  }

  function videoForAction(item) {
    var deferred = nativeVideoDeferred(item);
    if (!deferred) return null;
    if (!deferred.getAttribute('loaded') && typeof deferred.loadContent === 'function') deferred.loadContent(false);
    var v = deferred.querySelector('video');
    wireVideo(v);
    if (v) {
      v.playsInline = true;
      v.setAttribute('playsinline', '');
    }
    return v;
  }

  function onSoundClick(item) {
    var v = videoForAction(item);
    if (!v) return;
    if (soundOn(v)) {
      resetSound(v);
      if (v.paused && visible.has(item)) playVideo(v);
      return;
    }
    v.dataset.ohliSoundOn = '1';
    v.defaultMuted = false;
    v.muted = false;
    updateActions(v);
    var attempt = v.play(); // executado dentro do gesto; necessário no WebKit
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function (err) {
        dbg('som bloqueado ' + (err && err.name));
        resetSound(v);
        v.dataset.ohliUserPaused = '1';
        updateActions(v);
      });
    }
  }

  // Fonte única da verdade: cada slide visível toca (e permitido); os que saíram
  // da viewport pausam. Idempotente e seguro para ser chamado em rajada.
  function sync() {
    targets.forEach(function (item) {
      var deferred = nativeVideoDeferred(item);
      if (!deferred) return;
      if (document.hidden || modalOpen()) {
        var covered = deferred.querySelector('video');
        if (covered) {
          resetSound(covered);
          if (!covered.paused) {
            covered.dataset.ohliProgrammatic = '1';
            covered.pause();
          }
          updateActions(covered);
        }
        return;
      }
      if (visible.has(item)) {
        if (!deferred.getAttribute('loaded')) {
          customElements.whenDefined('deferred-media').then(function () {
            if (
              visible.has(item) &&
              !document.hidden &&
              !modalOpen() &&
              !deferred.getAttribute('loaded') &&
              typeof deferred.loadContent === 'function'
            ) {
              dbg('loadContent()');
              deferred.loadContent(false); // false = não roubar o foco/scroll
              var v = deferred.querySelector('video');
              dbg('loaded video=' + !!v + ' mutedProp=' + (v && v.muted) + ' inline=' + (v && v.playsInline));
              wireVideo(v);
              playVideo(v);
            }
          });
        } else {
          var v = deferred.querySelector('video');
          wireVideo(v);
          if (v && v.paused && drivable(v)) {
            if (v.ended) v.currentTime = 0;
            playVideo(v);
          }
        }
      } else {
        var vv = deferred.querySelector('video');
        if (vv) {
          // Fora da viewport, som e pausa manual voltam ao padrão mudo.
          resetSound(vv);
          delete vv.dataset.ohliUserPaused;
          if (!vv.paused) {
            vv.dataset.ohliProgrammatic = '1'; // marca: pausa nossa, não externa
            vv.pause();
          }
          updateActions(vv);
        }
      }
    });
  }

  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
        dbg('IO ' + entry.isIntersecting + ':' + entry.intersectionRatio.toFixed(2));
      });
      sync();
    },
    { threshold: THRESHOLD }
  );

  // (Re)escaneia a galeria: poda os slides que saíram do DOM (re-render de seção
  // no editor de tema) e passa a observar os de vídeo nativo que entraram.
  function rescan() {
    for (var i = targets.length - 1; i >= 0; i--) {
      if (!targets[i].isConnected) {
        io.unobserve(targets[i]);
        visible.delete(targets[i]);
        targets.splice(i, 1);
      }
    }
    Array.prototype.slice
      .call(document.querySelectorAll('media-gallery .product__media-item'))
      .forEach(function (item) {
        if (targets.indexOf(item) === -1 && nativeVideoDeferred(item)) {
          targets.push(item);
          // Habilita o CSS que mostra o vídeo inline no mobile (senão fica
          // display:none no layout stacked e o iOS não toca).
          item.classList.add('ohli-inline-video');
          var sound = action(item, 'sound');
          if (sound) sound.addEventListener('click', function () { onSoundClick(item); });
          io.observe(item);
        }
      });
    dbg('rescan · targets=' + targets.length);
  }

  window.__ohliPdpVideoAutoplay = { rescan: rescan, io: io, sync: sync };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rescan);
  } else {
    rescan();
  }

  // Editor de tema / re-render de seção: observa nós que passaram a existir.
  document.addEventListener('shopify:section:load', rescan);

  document.addEventListener('visibilitychange', sync);
  document.body.addEventListener('modalClosed', function () {
    setTimeout(sync, 0);
  });
})();
